import { Box, Button, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { ShouldRevalidateFunctionArgs, useFetcher, useLoaderData } from "react-router-dom";
import { useGlobal } from "../../components/GlobalProviderLayout";
import { CLOSE_TOAST, IToast, OPEN_TOAST } from "../../components/GlobalProviderLayout/GlobalProviderLayoutModel";
import { arraysAreEqual, packObjForSubmit, unpackObjForSubmit } from "../../utils";
import { doFetch } from "../../utils/doFetch";
import { doFetchPut } from "../../utils/doFetchPut";
import { DRPluginAvailability } from "./DRPluginAvailability";
import { IDRPluginAvailabilityData, IShift } from "./DRPluginAvailabilityData";

export const DRPluginAvailabilityRouteLoader = async ({ request }: { request: any, params: any }) =>
  await doFetch(`dr-plugin-availability`, { signal: request.signal }, true, false);

export const DRPluginAvailabilityRouteAction = async ({ request }: { request: any, params: any }) => {
  let formData = await request.formData();
  let obj = JSON.parse(unpackObjForSubmit(formData));
  return doFetchPut(
    'dr-plugin-availability',
    { signal: request.signal },
    obj
  );
}

export const DRPluginAvailabilityRouteShouldRevalidate = ({ actionResult }: ShouldRevalidateFunctionArgs) =>
  !actionResult?.error

export const DRPluginAvailabilityRoute = () => {
  const fetcher = useFetcher();
  const loaderData = useLoaderData();
  const global = useGlobal();

  const data = fetcher.data?.shifts ? fetcher.data as IDRPluginAvailabilityData :
    loaderData as IDRPluginAvailabilityData;

  const [shifts, setShifts] = useState<IShift[]>(data.shifts ?? []);

  useEffect(() => {
    if (fetcher.data?.error && !global.state.toast.open) {
      const payload: IToast = {
        title: <Typography sx={{ mb: 1, fontWeight: 'bold' }}>The declaration window has expired for this Shift!</Typography>,
        msg: <Box>
          <Typography sx={{ mb: 1 }}>You won't be able to submit your changes at this time. </Typography>
          <Typography sx={{ mb: 1 }}>The page will refresh when you click on the [OK] button below.</Typography>
        </Box>,
        severity: "error",
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'center'
        },
        closeTime: 0,
        action: <Button
          onClick={() => {
            fetcher.load('/dr-plugin-availability');
          }}
        >
          OK
        </Button>
      };

      global.dispatch({ type: OPEN_TOAST, payload });
    } else if (!!fetcher.data?.shifts && global.state.toast.open) {
      global.dispatch({ type: CLOSE_TOAST })
    }
  }, [fetcher.data?.error, fetcher, global])

  useEffect(() => {
    if (data.shifts) {
      setShifts(data.shifts);
    }
  }, [data.shifts]);

  const onSelectShift = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    setShifts(prev => prev.map(s => s.despatchId === value ? { ...s, isSelected: checked } : s));
  };

  const onRefresh = () => fetcher.load('/dr-plugin-availability')

  const onSubmit = async () => {
    const formData = packObjForSubmit({
      shifts: shifts.map(s => ({
        pluggedIn: s.isSelected,
        shiftDate: s.shiftDate,
        epdDespatchTypeId: s.codeEpdDespatchTypeRecordId,
        codeDespatchShiftRecordId: s.codeDespatchShiftRecordId,
        OtherTimeNumber: s.otherTimeNumber
      }))
    });

    await fetcher.submit(formData, {
      method: "put",
      action: '/dr-plugin-availability'
    });
  }

  return (
    <DRPluginAvailability
      data={data}
      shifts={shifts}
      hasChanges={data.shifts && !arraysAreEqual(data.shifts, shifts)}
      onRefresh={onRefresh}
      onSelectShift={onSelectShift}
      onSubmit={onSubmit}
    />
  )
}